import { int } from "@goodgamestudios/math-utilities";

import { FrameEvent } from "../../event/FrameEvent";

import BitmapData = createjs.BitmapData;
import MovieClip = createjs.MovieClip;
import Container = createjs.Container;
import EventDispatcher = createjs.EventDispatcher;
import Rectangle = createjs.Rectangle;

export class AnimatedDisplayObject extends EventDispatcher {
    // public scaleMatrix: Matrix;

    public frameRanges: any[]; // bereich in an frames die im Loop gespielt werden sollen. Usage: [[startFrame,endFrame],[startFrame,endFrame]]
    private _currentFrameRange: number = 0; // aktueller Bereich der gerade geloopt wird

    protected _lastFrameStepTime: number = NaN;
    protected _startTime: number = NaN;
    protected _id: number = 0;
    protected _name: string;
    protected _timePerFrame: number = 50;
    protected _frameRate: number = 15;
    protected _frameAmount: number = 0;
    protected _frameNr: number = 0;
    protected _isPlaying: boolean = false;
    protected _bounds: Rectangle;
    protected _hasAnimChilds: boolean = false;
    protected _loops: number = 0;

    constructor(loops: number, id: number, name: string) {
        super();
        this._id = id;
        this._name = name;
        this._loops = loops;
        this._frameNr = 1;
    }

    /**
     * Processes the source movieclip.
     *
     * @param mc the source mc
     * @param m  the transformation matrix
     */
    public processAnimation(mc: MovieClip, m: any /*Matrix*/ = null, hasAnimChilds: boolean = false): void {
        // this.scaleMatrix = m;
    }

    /**
     * Processes the source movieclip.
     *
     * @param mc the source mc
     * @param m  the transformation matrix
     * @param multiLoop: you can use this if you have several Movieclips in one and want them to be wraped
     */

    public processMultiLoopAnimation(mc: MovieClip, m: any /*Matrix*/ = null): void {
        // this.scaleMatrix = m;
    }

    public setFrameRate(fps: number): void {
        this._frameRate = fps;
        this._timePerFrame = 1000 / fps;
    }

    public get id(): number {
        return this._id;
    }

    public get name(): string {
        return this._name;
    }

    public get lengthInMillis(): number {
        return this.frameAmount * this._timePerFrame;
    }

    public get firstFrame(): number {
        if (this.frameRanges)
            return this.frameRanges[this._currentFrameRange][0];
        else
            return 1;
    }

    public get frameAmount(): number {
        if (this.frameRanges)
            return this.frameRanges[this._currentFrameRange][1] - this.frameRanges[this._currentFrameRange][0] + 1;
        else
            return this._frameAmount;
    }

    public get disp(): Container {
        // implement in subclass
        return null;
    }
    public get colorLayer(): any {
        return null;
    }
    public get frameRate(): number {
        return this._frameRate;
    }

    public get frameNr(): number {
        return this._frameNr;
    }

    public get bounds(): Rectangle {
        return this._bounds;
    }

    public isPlaying(): boolean {
        return this._isPlaying;
    }

    /**
     * Starts the playback of the animated movieclip. If the animated
     * movieclip is already playing while calling this method, it calls
     * stop() and then play again instantly to allow for framerate
     * changes during playback.
     */
    public play(): void {
        if (this._isPlaying) {
            this.reset();
            this.play();
        }
        else {
            this._lastFrameStepTime = Date.now();
            this._frameNr = int(this.frameRanges ? this.frameRanges[this._currentFrameRange][0] : 1);
            this._startTime = this._lastFrameStepTime - ((this._frameNr - 1) * this._timePerFrame);
            this._isPlaying = true;
        }
    }

    /**
     * Stops the playback of the animated bitmap.
     */
    public stop(): void {
        this._isPlaying = false;
    }

    /**
     * Resets the playback to the first frame.
     * @author Dominik Willers
     */
    public reset(): void {
        this.stop();
        this._frameNr = this.firstFrame;
        this.draw();
    }

    /**
     * Jumps to the specified frameNr and plays the animated
     * bitmap from that position. Note that the frames of an
     * animated bitmap start at 1.
     *
     * @param frameNr The frame number to which to jump.
     */
    public gotoAndPlay(frameNr: number): void {
        this._frameNr = frameNr;
        this.play();
    }

    /**
     * Jumps to the specified frameNr and stops the animated
     * bitmap at that position. Note that the frames of an
     * animated bitmap start at 1.
     *
     * @param frameNr The frame number to which to jump.
     */
    public gotoAndStop(frameNr: number): void {
        if (frameNr < 1 || frameNr > this.frameAmount)
            return;
        this._frameNr = frameNr;
        this.stop();
        this.draw();
    }

    public gotoAndStopAtFirst(): void {
        this._frameNr = this.firstFrame;
        this.stop();
        this.draw();
    }

    public gotoAndStopAtLast(): void {
        this._frameNr = this.frameAmount;
        this.stop();
        this.draw();
    }
    /**
     * Plays the animation forward by one frame.
     * @private
     */
    public playForward(): void {
        if (!this.isPlaying()) return;

        var runTime: number = Date.now() - this._startTime;
        var loopsPlayed: number = Math.floor(runTime / this.lengthInMillis);
        // calculate next frame number to display
        var newFrame: number = Math.max(1, Math.ceil((runTime - (this.lengthInMillis * loopsPlayed)) / this._timePerFrame)) + this.firstFrame - 1;

        // time up to display next frame?
        if (this._frameNr > newFrame || (loopsPlayed >= this._loops && this._loops != -1)) {
            // dispatch an ENTER Event for every frame between current frame and next frame to display
            while (this._frameNr < this.frameAmount) {
                this._frameNr++;
                this.dispatchFrameEvent(FrameEvent.ENTER);
                // did a frame event listener stop the movie?
                if (!this.isPlaying()) return;
            }
            // another loop left or endless loop (loops == -1) ?
            this._isPlaying = (this._loops > loopsPlayed || this._loops == -1);
            this.dispatchFrameEvent(FrameEvent.LOOP_END);
            // did a frame event listener stop the movie?
            if (!this.isPlaying()) {
                this.dispatchFrameEvent(FrameEvent.END);
                return;
            }
            // set frame nr to 0 for the new beginning loop
            this._frameNr = this.firstFrame;
        }
        // dispatch an ENTER Event for every frame between current frame and next frame to display
        while (this._frameNr < newFrame) {
            this._frameNr++;
            this.dispatchFrameEvent(FrameEvent.ENTER);
            // did a frame event listener stop the movie?
            if (!this.isPlaying()) return;
        }
        this.draw();
    }

    public getCurrentFrame(): BitmapData {
        return null;
    }

    /**
     * Plays the animation backwards by one frame.
     * @private
     */
    //private function playBackward():void
    //{
    //	_frameNr--;
    //	if (_frameNr < 1) _frameNr = _frameAmount;
    //	draw();
    //}

    /**
     * Draws the next frame.
     * @private
     */
    protected draw(): void {
        // implement in subclass
    }

    protected dispatchFrameEvent(type: string): void {
        // implement in subclass
    }

    public set currentFrameRange(value: number) {
        if (this.frameRanges && value < this.frameRanges.length)
            this._currentFrameRange = value;
        else
            this._currentFrameRange = 0;
    }
    public get currentFrameRange(): number {
        return this._currentFrameRange;
    }
}
